/**
 * Reusable editorial banner — the "LiveExamples" treatment.
 * Wide aspect-ratio image with a multi-color gradient overlay,
 * a kicker label, a bold headline, and 3 colored chips on the right.
 *
 * Drop it in between section header and main grid for consistent rhythm.
 */
const EditorialBanner = ({
  image,
  kicker,
  headline,
  chips = [],
  variant = "dark", // "dark" | "light"
  gradient = "violet-cyan", // preset overlay tones
  testId = "editorial-banner",
}) => {
  const GRADIENTS = {
    "violet-cyan":
      "linear-gradient(90deg, rgba(13,14,22,0.95) 0%, rgba(13,14,22,0.55) 35%, rgba(124,58,237,0.35) 75%, rgba(0,229,255,0.35) 100%)",
    "gold-rose":
      "linear-gradient(90deg, rgba(13,14,22,0.95) 0%, rgba(13,14,22,0.55) 35%, rgba(245,158,11,0.45) 75%, rgba(244,63,94,0.45) 100%)",
    "emerald-cyan":
      "linear-gradient(90deg, rgba(13,14,22,0.95) 0%, rgba(13,14,22,0.55) 35%, rgba(16,185,129,0.45) 75%, rgba(0,229,255,0.45) 100%)",
    "indigo-rose":
      "linear-gradient(90deg, rgba(13,14,22,0.95) 0%, rgba(13,14,22,0.55) 35%, rgba(0,71,255,0.40) 75%, rgba(244,63,94,0.40) 100%)",
    "paper-violet":
      "linear-gradient(90deg, rgba(252,252,253,0.96) 0%, rgba(252,252,253,0.60) 35%, rgba(124,58,237,0.20) 75%, rgba(0,229,255,0.20) 100%)",
  };

  const onPaper = variant === "light";
  const borderClr = onPaper ? "border-[#e4e4e7]" : "border-white/10";
  const kickerClr = onPaper ? "text-[#7c3aed]" : "text-[#00e5ff]";
  const headlineClr = onPaper ? "text-[#090a0f]" : "text-white";
  const chipBg = onPaper ? "bg-white/70" : "bg-white/8";
  const chipBorder = onPaper ? "border-[#e4e4e7]" : "border-white/20";
  const chipText = onPaper ? "text-[#090a0f]" : "text-white";

  return (
    <div
      data-testid={testId}
      className={`relative w-full aspect-[24/7] overflow-hidden border ${borderClr} mb-px`}
    >
      <img
        src={image}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />
      <div
        className="absolute inset-0"
        style={{ background: GRADIENTS[gradient] || GRADIENTS["violet-cyan"] }}
      />
      <div className="absolute inset-0 flex items-center justify-between p-6 lg:p-10">
        <div className="max-w-xl">
          <p
            className={`font-mono text-[11px] tracking-[0.22em] uppercase mb-2 ${kickerClr}`}
          >
            {kicker}
          </p>
          <p
            className={`font-display font-bold ${headlineClr} text-[22px] lg:text-[30px] tracking-tight leading-tight`}
          >
            {headline}
          </p>
        </div>
        {chips.length > 0 && (
          <div className="hidden lg:flex flex-col items-end gap-2">
            {chips.map((b) => (
              <span
                key={b.t}
                className={`inline-flex items-center gap-2 px-3 py-1.5 ${chipBg} border ${chipBorder} font-mono text-[10px] tracking-[0.18em] uppercase ${chipText}`}
                style={{ borderColor: `${b.c}40` }}
              >
                <span className="w-1.5 h-1.5" style={{ backgroundColor: b.c }} />
                {b.t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorialBanner;
